import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import { teamPaymentDetailPath, teamPaymentsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface PaymentRow {
  date: string
  amount: number
  employee_id?: number
  employee_name?: string
}

interface EmployeeOption {
  id: number
  name: string
}

interface Props {
  employee: { id: number; name: string }
  payments: PaymentRow[]
  start_date: string
  end_date: string
  total: number
  can_pay_expenses: boolean
  selected_employee_id: number | null
  employees?: EmployeeOption[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Constants ──────────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team", href: "/team" },
  { title: "Payment History", href: teamPaymentsPath() },
]

function buildColumns(
  startDate: string,
  endDate: string,
  showEmployee: boolean,
  employeeId: string,
): HpiColumn<PaymentRow>[] {
  const cols: HpiColumn<PaymentRow>[] = [
    {
      header: "Date",
      width: "w-[140px] !flex-none",
      render: (row) => (
        <Link
          href={teamPaymentDetailPath({
            date: row.date,
            start_date: startDate,
            end_date: endDate,
            employee_id: row.employee_id?.toString() ?? employeeId,
          })}
          className="text-sm text-blue-600 hover:underline"
        >
          {formatDate(row.date)}
        </Link>
      ),
    },
  ]

  if (showEmployee) {
    cols.push({
      header: "Employee",
      render: (row) => (
        <Link
          href={teamPaymentDetailPath({
            date: row.date,
            start_date: startDate,
            end_date: endDate,
            employee_id: row.employee_id?.toString() ?? employeeId,
          })}
          className="text-sm text-blue-600 hover:underline"
        >
          {row.employee_name ?? ""}
        </Link>
      ),
    })
  }

  cols.push({
    header: "Amount",
    width: "w-[140px] !flex-none",
    align: "right",
    render: (row) => (
      <Link
        href={teamPaymentDetailPath({
          date: row.date,
          start_date: startDate,
          end_date: endDate,
          employee_id: row.employee_id?.toString() ?? employeeId,
        })}
        className="text-sm font-medium text-blue-600 hover:underline"
      >
        {formatCurrency(row.amount)}
      </Link>
    ),
  })

  return cols
}

/* ── Component ──────────────────────────────────────────── */

export default function Payments({
  employee,
  payments,
  start_date,
  end_date,
  total,
  can_pay_expenses,
  selected_employee_id,
  employees,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [selectedEmployee, setSelectedEmployee] = useState(
    String(selected_employee_id ?? employee.id),
  )

  const viewingAll = selected_employee_id === null || selected_employee_id === 0

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (can_pay_expenses && selectedEmployee) {
      params.employee_id = selectedEmployee
    }
    router.get(teamPaymentsPath(), params, { preserveState: true })
  }

  const employeeOptions = employees
    ? [
        { value: "0", label: "All" },
        ...employees.map((emp) => ({ value: String(emp.id), label: emp.name })),
      ]
    : []

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Payment History" />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          {can_pay_expenses && employeeOptions.length > 0 && (
            <HpiFilterSelect
              label="Employee"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              options={employeeOptions}
              placeholder="All"
            />
          )}
        </HpiDateFilterBar>

        <HpiDataTable<PaymentRow>
          title={`Expense Payments - ${employee.name}`}
          columns={buildColumns(
            startDate,
            endDate,
            viewingAll,
            String(selected_employee_id ?? employee.id),
          )}
          data={payments}
          keyFn={(row) => `${row.date}-${row.employee_id ?? 0}`}
        />

        {payments.length > 0 && (
          <HpiStatsBar
            className="mt-6"
            stats={[
              {
                label: "Total",
                subLabel: "Payments",
                value: formatCurrency(total),
              },
            ]}
          />
        )}
      </div>
    </AppLayout>
  )
}
